// src/pages/DrawResult/index.tsx
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { Container, Row, Col, Card, CardHeader, CardBody, CardFooter, Button, Input } from 'reactstrap';
import BreadCrumb from 'Components/Common/BreadCrumb';
import { fetchIssuancePlanOptions, fetchDrawResultByPlan, saveDrawResult } from 'slices/drawResult/thunk';
import { setSelectedIssuancePlanId, setRegion } from 'slices/drawResult/reducer';
import { DrawResultTier, IssuancePlanOption } from './types';
import { fetchSystemRegion } from 'Components/constants/regions';
import { useBrandingTitle } from '../../hooks';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('draw_result');

const DrawResultPage: React.FC = () => {
  useBrandingTitle(st('ket_qua_quay_so_text'));

  const location = useLocation();
  const dispatch = useDispatch();
  const { region, issuancePlanOptions, selectedIssuancePlanId, summary, loading, submitting } = useSelector(
    (state: any) => state.DrawResult
  );

  // Draft số trúng đang nhập, key theo prizeTierCode — đồng bộ lại mỗi khi đổi kỳ/tải xong dữ liệu
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [errors, setErrors] = useState<Record<string, string | null>>({});

  // Yêu cầu bổ sung (README_UPDATE.md) — Miền phải lấy từ ĐÚNG 1 nguồn cấu hình (API), không đoán từ
  // cache. Chỉ load dữ liệu kết quả quay số SAU KHI xác nhận được Miền, để không load nhầm miền khác.
  const [regionConfirmed, setRegionConfirmed] = useState(false);
  const [regionError, setRegionError] = useState<string | null>(null);

  // Mục 1 (README.md §7) — "clear data grid view trước khi vẽ lại": chỉ vẽ MỖI hạng giải đúng 1 lần.
  // Nếu API lỡ trả về entry trùng prizeTierCode (dữ liệu cấu hình cũ + mới chồng lên nhau) thì loại bỏ
  // bản trùng ở đây trước khi dựng grid, tránh render lặp ra rất nhiều ô nhập kết quả.
  // Mục 8.1 (README_UPDATE.md) — kẹp thêm quantity ở FE (BE đã kẹp 100) phòng dữ liệu cũ: một hạng
  // giải có Quantity lỗi (quá lớn) không được phép render ra hàng trăm ô nhập.
  const MAX_CELLS_PER_TIER = 100;
  const visibleEntries = useMemo<DrawResultTier[]>(() => {
    if (!summary) return [];
    const seen = new Set<string>();
    const list: DrawResultTier[] = [];
    summary.entries.forEach((tier: DrawResultTier) => {
      if (seen.has(tier.prizeTierCode)) return;
      seen.add(tier.prizeTierCode);
      list.push({ ...tier, quantity: Math.min(Math.max(tier.quantity || 0, 0), MAX_CELLS_PER_TIER) });
    });
    return list;
  }, [summary]);

  // Mục 3 (README.md §7) — điều hướng bằng Enter giữa các ô nhập (hạn chế thao tác chuột).
  // inputRefs là mảng phẳng theo đúng thứ tự hiển thị: [tier0.ô0, tier0.ô1, ... tier1.ô0, ...].
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const saveButtonRef = useRef<HTMLButtonElement | null>(null);

  // Lấy Miền từ NGUỒN CẤU HÌNH DUY NHẤT (GET /system-api/SystemConfiguration/region) mỗi khi vào
  // trang. Không dùng cache/đoán — cache có thể hết hạn/lệch làm load nhầm dữ liệu miền khác.
  useEffect(() => {
    let alive = true;
    fetchSystemRegion().then((r) => {
      if (!alive) return;
      if (r) {
        if (r !== region) dispatch(setRegion(r));
        setRegionError(null);
        setRegionConfirmed(true);
      } else {
        setRegionConfirmed(false);
        setRegionError(st('he_thong_chua_cau_hinh_mien_text'));
      }
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(fetchIssuancePlanOptions() as any);
  }, [dispatch]);

  useEffect(() => {
    if (regionConfirmed && selectedIssuancePlanId) {
      dispatch(fetchDrawResultByPlan({ issuancePlanId: selectedIssuancePlanId, region }) as any);
    }
  }, [dispatch, regionConfirmed, selectedIssuancePlanId, region]);

  useEffect(() => {
    // Reset toàn bộ ô nhập theo đúng danh sách hạng giải hiện tại (đã khử trùng ở visibleEntries).
    inputRefs.current = [];
    const next: Record<string, string[]> = {};
    visibleEntries.forEach((tier: DrawResultTier) => {
      const numbers = Array.from({ length: tier.quantity }, (_, i) => tier.winningNumbers[i] || '');
      next[tier.prizeTierCode] = numbers;
    });
    setDraft(next);
    setErrors({});
  }, [visibleEntries]);

  const handleChangeKy = (e: React.ChangeEvent<HTMLInputElement>) => {
    dispatch(setSelectedIssuancePlanId(Number(e.target.value)));
  };

  const handleNumberChange = (tierCode: string, index: number, value: string) => {
    const digitsOnly = value.replace(/\D/g, '');
    setDraft((prev) => {
      const numbers = [...(prev[tierCode] || [])];
      numbers[index] = digitsOnly;
      return { ...prev, [tierCode]: numbers };
    });
  };

  const validateTier = useCallback(
    (tier: DrawResultTier): string | null => {
      const numbers = draft[tier.prizeTierCode] || [];
      if (numbers.some((n) => n.length !== tier.digitCondition)) {
        return st('moi_so_phai_dung_digit_condition_text', { digit_condition: tier.digitCondition });
      }
      if (numbers.some((n) => n.length === 0)) {
        return st('chua_nhap_du_so_trung_text');
      }
      // Mục 2 (README.md §8.2) — trong cùng 1 giải của 1 kỳ không được có 2 số TRÙNG nhau
      // (đã bỏ check "liền kề" theo phản hồi).
      if (new Set(numbers).size !== numbers.length) {
        return st('trong_cung_mot_giai_khong_duoc_text');
      }
      return null;
    },
    [draft]
  );

  const handleBlurTier = (tier: DrawResultTier) => {
    setErrors((prev) => ({ ...prev, [tier.prizeTierCode]: validateTier(tier) }));
  };

  const isFormValid = () => {
    return visibleEntries.length > 0 && visibleEntries.every((tier) => validateTier(tier) === null);
  };

  const handleSave = () => {
    if (!selectedIssuancePlanId || visibleEntries.length === 0) return;
    const nextErrors: Record<string, string | null> = {};
    visibleEntries.forEach((tier: DrawResultTier) => {
      nextErrors[tier.prizeTierCode] = validateTier(tier);
    });
    setErrors(nextErrors);
    if (Object.values(nextErrors).some((e) => e !== null)) return;

    const entries = visibleEntries.map((tier: DrawResultTier) => ({
      prizeTierCode: tier.prizeTierCode,
      winningNumbers: draft[tier.prizeTierCode] || [],
    }));
    dispatch(saveDrawResult({ issuancePlanId: selectedIssuancePlanId, region, entries }) as any);
  };

  // Vị trí phẳng của ô (tierIndex, idx) trong toàn bộ form — dùng cho điều hướng bằng Enter.
  const flatIndexOf = (tierIndex: number, idx: number) =>
    visibleEntries
      .slice(0, tierIndex)
      .reduce((sum, t) => sum + (draft[t.prizeTierCode]?.length || 0), 0) + idx;

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, flat: number) => {
    if (e.key !== 'Enter') return;
    // Enter = chuyển sang ô kế tiếp (không submit form). Ô cuối cùng thì focus nút "Lưu kết quả".
    e.preventDefault();
    const nextInput = inputRefs.current[flat + 1];
    if (nextInput) {
      nextInput.focus();
      nextInput.select();
    } else {
      saveButtonRef.current?.focus();
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('ket_qua_quay_so_text')} pageTitle={st('tra_thuong_text')} />

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                  <h5 className="card-title mb-0 d-flex align-items-center gap-2">
                    {st('ket_qua_quay_so_text')}{summary?.issuancePlanCode ? st('ky_issuance_plan_code_text', { issuance_plan_code: summary.issuancePlanCode }) : ''}
                    <span className="badge bg-primary-subtle text-primary fs-12 fw-normal">{region}</span>
                  </h5>
                  <div className="d-flex align-items-center gap-2">
                    <Input type="select" style={{ width: 240 }} value={selectedIssuancePlanId || ''} onChange={handleChangeKy}>
                      {issuancePlanOptions.length === 0 && <option value="">{st('chua_co_ky_nao_option')}</option>}
                      {issuancePlanOptions.map((opt: IssuancePlanOption) => (
                        <option key={opt.id} value={opt.id}>
                          {opt.planCode} - {new Date(opt.expectedDrawDate).toLocaleDateString('vi-VN')}
                        </option>
                      ))}
                    </Input>
                  </div>
                </CardHeader>
                <CardBody>
                  {regionError ? (
                    <div className="alert alert-warning mb-0">
                      <i className="ri-error-warning-line align-bottom me-1" />
                      {regionError}
                    </div>
                  ) : loading ? (
                    <div className="text-center py-4">
                      <span className="spinner-border spinner-border-sm" />
                    </div>
                  ) : visibleEntries.length === 0 ? (
                    <div className="text-center text-muted py-4">
                      <i className="ri-inbox-line" style={{ fontSize: '48px' }}></i>
                      <p className="mt-2">{st('chua_co_ky_nao_duoc_chon_text')}</p>
                    </div>
                  ) : (
                    // Mỗi hạng giải căn giữa, cùng 1 kiểu input đồng nhất (giống các form khác trong hệ
                    // thống) — dáng "tháp" tự nhiên có được từ số ô mỗi hàng (ít ô ở Đặc biệt/Nhất/Nhì, nhiều
                    // ô ở Tư/KK), không cần cường điệu bằng màu sắc hay cỡ chữ riêng.
                    <div className="d-flex flex-column align-items-center">
                      {visibleEntries.map((tier: DrawResultTier, tierIndex: number) => {
                        const numbers = draft[tier.prizeTierCode] || [];
                        const error = errors[tier.prizeTierCode];
                        // Mục 2 (README.md §7) — ô nhập rộng theo số chữ số + tăng cỡ chữ/giãn ký tự để
                        // không bị che mất số ở các giải nhiều chữ số (Đặc biệt 6, Nhất/Nhì/Ba/Tư 5...).
                        const inputWidth = Math.max(96, tier.digitCondition * 22 + 28);
                        return (
                          <div key={tier.prizeTierCode} className="text-center mb-4">
                            <div className="mb-2">
                              <span className="fw-semibold fs-5">{tier.displayName}</span>
                              <span className="text-muted ms-2">
                                {tier.quantity} {st('so_text')} {tier.digitCondition} {st('chu_so_so_text')}
                              </span>
                            </div>
                            <div className="d-flex flex-wrap justify-content-center gap-2">
                              {numbers.map((num, idx) => {
                                const flat = flatIndexOf(tierIndex, idx);
                                return (
                                  <Input
                                    key={idx}
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={tier.digitCondition}
                                    placeholder={'0'.repeat(tier.digitCondition)}
                                    data-action-code="input-winning-number"
                                    data-action-name="Số trúng thưởng"
                                    value={num}
                                    innerRef={(el: HTMLInputElement | null) => {
                                      inputRefs.current[flat] = el;
                                    }}
                                    disabled={!PageActionPermission.canEnable(location.pathname, 'input-winning-number')}
                                    onChange={(e) => handleNumberChange(tier.prizeTierCode, idx, e.target.value)}
                                    onKeyDown={(e) => handleInputKeyDown(e, flat)}
                                    onBlur={() => handleBlurTier(tier)}
                                    invalid={!!error}
                                    className="text-center fw-semibold"
                                    style={{ width: inputWidth, fontSize: '1.05rem', letterSpacing: '3px' }}
                                  />
                                );
                              })}
                            </div>
                            {error && <div className="text-danger small mt-1">{error}</div>}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardBody>
                {visibleEntries.length > 0 && PageActionPermission.canDisplay(location.pathname, 'button-save') && (
                  <CardFooter className="d-flex justify-content-end gap-2">
                    <Button
                      color="primary"
                      innerRef={saveButtonRef}
                      data-action-code="button-save"
                      data-action-name="Lưu kết quả quay số"
                      onClick={handleSave}
                      disabled={submitting || !isFormValid()}
                    >
                      {submitting ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          {st('dang_luu_text')}
                        </>
                      ) : (
                        <>
                          <i className="ri-save-line me-1"></i>
                          {st('luu_ket_qua_text')}
                        </>
                      )}
                    </Button>
                  </CardFooter>
                )}
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default DrawResultPage;
